/**
 * Main Application Component
 * Implements the full Grossman-Miller Simulator layout
 */

import { useEffect } from 'react';
import { useSimulationStore } from '../state/simulationStore';
import { ParameterPanel } from './ParameterPanel';
import { SimulationControls } from './SimulationControls';
import { TimelineBar } from './TimelineBar';
import { PriceChart } from './PriceChart';
import { AgentCardsPanel } from './AgentCardsPanel';
import { FlowDiagram } from './FlowDiagram';
import { LiquidityDiscountPanel } from './LiquidityDiscountPanel';
import { SummaryStatsPanel } from './SummaryStatsPanel';
import { ExplanationPanel } from './ExplanationPanel';
import { FormulaOverlay } from './FormulaOverlay';

function Header() {
  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Grossman-Miller Simulator</h1>
            <p className="text-blue-200 text-sm mt-1">
              Interactive Market Making Model Visualization
            </p>
          </div>
          <div className="text-right text-sm">
            <a
              href="https://doi.org/10.1111/j.1540-6261.1988.tb04594.x"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-200 hover:text-white underline"
            >
              Grossman & Miller (1988)
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export function App() {
  const { loadFromUrl, simulation } = useSimulationStore();

  // Load parameters from URL on mount
  useEffect(() => {
    const queryString = window.location.search.slice(1);
    if (queryString) {
      loadFromUrl(queryString);
    }
  }, [loadFromUrl]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Panel - Parameters & Controls */}
          <div className="lg:col-span-3 space-y-6">
            <ParameterPanel />
            <SimulationControls />
          </div>

          {/* Right Panel - Visualizations */}
          <div className="lg:col-span-9 space-y-6">
            {/* Timeline */}
            <TimelineBar />

            {/* Main Visualizations Row */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <PriceChart />
              <FlowDiagram />
            </div>

            {/* Metrics Row */}
            {simulation && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <LiquidityDiscountPanel />
                <SummaryStatsPanel />
              </div>
            )}

            {/* Agent Cards */}
            <AgentCardsPanel />

            {/* Explanation */}
            <ExplanationPanel />
          </div>
        </div>
      </main>

      {/* Formula Overlay */}
      <FormulaOverlay />

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm">
          <p>
            Based on Grossman, S.J. and Miller, M.H. (1988), "Liquidity and Market
            Structure," <em>The Journal of Finance</em>, Vol. 43, No. 3, pp. 617-633.
          </p>
          <p className="mt-2 text-gray-500">
            Educational tool for understanding market microstructure
          </p>
        </div>
      </footer>
    </div>
  );
}
